import React, { useContext, useState } from "react";
import { Calendar, defaultCalendarStrings, Dropdown } from "@fluentui/react";
import studylistContext from "../../../../../context/studylist/StudylistContext";
import themeContext from "../../../../../context/theme/ThemeContext";
import { t } from "i18next";
import Moment from "moment";
import "./DateRangePicker.css";

const calendarStyle = {
  root: {
    color: "#A19F9D",
    background: "transparent",
  },
  goTodayButton: {
    color: "#A19F9D",
  },
  headerIconButton: {
    color: "#A19F9D",
  },
  daySelected: {
    background: "#5A5A5A !important",
    color: "#ffffff",
  },
  dayIsToday: {
    background: "red",
  },
  liveRegion: {
    color: "#A19F9D",
  },
  weekDayLabelCell: {
    paddingLeft: "2px",
  },
};

const dropdownOptions = [
  { key: "from", text: "From" },
  { key: "to", text: "To" },
];

const sameDay = (a, b) => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const isBetween = (date, from, to) => {
  if (!from || !to) return false;
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const f = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const t2 = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return d > f && d < t2;
};

const applyRangeClasses = (el, date, from, to) => {
  if (!el) return;
  el.classList.remove("drp-day-in-range", "drp-day-range-edge");
  if (sameDay(date, from) || sameDay(date, to)) {
    el.classList.add("drp-day-range-edge");
  } else if (isBetween(date, from, to)) {
    el.classList.add("drp-day-in-range");
  }
};

const toZeroTime = (date) => {
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const DateRangePicker = ({ variant = "report" }) => {
  const {
    studyDateFrom, studyDateTo, setStudyDateFrom, setStudyDateTo, setStudyDate,
    reportDateFrom, reportDateTo, setReportDateFrom, setReportDateTo, setReportDate,
    searchDto,
  } = useContext(studylistContext);

  const { themeName } = useContext(themeContext);

  const isStudy = variant === "study";

  const fromDate  = isStudy ? studyDateFrom  : reportDateFrom;
  const toDate    = isStudy ? studyDateTo    : reportDateTo;
  const setFrom   = isStudy ? setStudyDateFrom  : setReportDateFrom;
  const setTo     = isStudy ? setStudyDateTo    : setReportDateTo;
  const setDate   = isStudy ? setStudyDate      : setReportDate;

  const dtoFromKey = isStudy ? "studyDateFrom" : "reportDateFrom";
  const dtoToKey   = isStudy ? "studyDateTo"   : "reportDateTo";
  const headerKey  = isStudy ? "study-list.filters.study-date" : "study-list.filters.report-date";

  const isLight = themeName === "light";
  const chipBg = isLight ? "white" : "black";
  const chipColor = isLight ? "black" : "white";

  const dropdownStyles = {
    root: { width: "100px", marginBottom: "4px" },
    title: { color: chipColor },
    dropdown: { background: chipBg },
    dropdownOptionText: { color: chipColor },
  };

  const [activeEnd, setActiveEnd] = useState("from");
  const [errorMsg, setErrorMsg] = useState("");

  const handleDayClick = (date) => {
    setErrorMsg("");
    const dateZero = toZeroTime(date);

    if (activeEnd === "from") {
      if (searchDto?.[dtoToKey] && dateZero > toZeroTime(toDate)) {
        setErrorMsg(t("study-list.filters.date-range-error-from"));
        return;
      }
      setFrom(date);
      setDate(date, true);
    } else {
      if (searchDto?.[dtoFromKey] && dateZero < toZeroTime(fromDate)) {
        setErrorMsg(t("study-list.filters.date-range-error-to"));
        return;
      }
      setTo(date);
      setDate(date, false);
    }
  };

  const activeDate = activeEnd === "from" ? fromDate : toDate;
  const activeValueLabel = activeDate
    ? Moment(activeDate).format("DD/MM/YYYY")
    : "DD/MM/YYYY";

  return (
    <div className="drp-wrapper">
      <span className="filter-section-text">{t(headerKey)}</span>
      <div className="drp-dropdown-row">
        <Dropdown
          options={dropdownOptions}
          selectedKey={activeEnd}
          onChange={(e, opt) => { setActiveEnd(opt.key); setErrorMsg(""); }}
          styles={dropdownStyles}
        />
        <span className="drp-active-value" style={{ background: chipBg, color: chipColor }}>{activeValueLabel}</span>
      </div>
      {errorMsg && <span className="drp-error-msg">{errorMsg}</span>}
      <div
        className="modal-scroll study-list-filter-section-without-search"
        style={{height:"unset"}}
        >
      <div className="drp-calendar-wrapper">
        <Calendar
          showMonthPickerAsOverlay={true}
          placeholder={t("placeholder.select-date")}
          ariaLabel="Select a date"
          styles={calendarStyle}
          calendarDayProps={{
            styles: {
              root: { padding: "0px 2px 0px 5px !important" },
            },
          }}
          showGoToToday={false}
          strings={defaultCalendarStrings}
          value={activeEnd === "from" ? (fromDate || toDate || new Date()) : (toDate || fromDate || new Date())}
          onSelectDate={handleDayClick}
          customDayCellRef={(el, date) =>
            applyRangeClasses(el, date, fromDate, toDate)
          }
        />
      </div>
      </div>
    </div>
  );
};

export default DateRangePicker;